import React, { useEffect } from 'react';
import { useTranslate, usePermissions } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import { Paper } from '@material-ui/core';

import { VirtualizedList, Title, Loader } from '~/components';
import { NoPermissions } from '~/components/ra';
import VirtualizedListWrapper from '~/components/VirtualizedListWrapper';
import {
  selectors,
  getVirtualizedListBegin,
  getVirtualizedListSuccess,
  getVirtualizedListFail,
} from '~/ducks/virtualizedList';

import { getPendingApprovals, ApprovalCaseFilters } from '~/api/approvals';
import { ApprovalCase } from '~/types/approvalCase';
import ListFilters from './components/QueueListFilters';
import CustomCell from './components/CustomCell';
import { getTypeOfIdentity } from '~/utils/identities';
import TenantLicensePaywall from '~/components/TenantLicensePaywall';
import { useCheckApprovalCasesLicence } from '~/hooks/useCheckTenantLicense';
import { invalidateEventEntities } from '~/hooks/eventEntities';

const resource = 'approvals/queue';
const i18nPath = `resources.approvals.`;

interface Props {
  history: any;
}

const QueueApprovalsList = (props: Props) => {
  const { history } = props;
  const { permissions } = usePermissions();

  const translate = useTranslate();
  const dispatch = useDispatch();
  const filters = useSelector(selectors.getFilters<ApprovalCaseFilters>(resource));
  const list = useSelector(selectors.getList(resource));
  const { isFeatureEnabled, isLoading } = useCheckApprovalCasesLicence();

  const firstEntering = history.action !== 'POP';
  useEffect(() => {
    if (firstEntering) invalidateEventEntities();
  }, [firstEntering]);

  const loadNextPage = async () => {
    if (isNextPageLoading || (!list.cursors.next && list.data.length > 0)) return;

    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource, queryToken }));

      const { data } = await getPendingApprovals({
        next: list.cursors.next,
        filters,
      });
      const responseData = {
        ...data,
        cursors: data.cursors,
        records: data.items as any[],
      };

      dispatch(getVirtualizedListSuccess({ resource, responseData, queryToken }));
    } catch (error) {
      const err = error as Error;
      dispatch(getVirtualizedListFail({ resource, error: err, queryToken }));
    }
  };

  const columns = [
    {
      id: 'submittedAt',
      header: translate(i18nPath + 'list.submittedAt'),
      selectCellData: (r: ApprovalCase) => r.submittedAt,
      cellDataWrapperComponent: CustomCell,
      style: {
        flexBasis: 150,
        minWidth: 100,
        maxWidth: 150,
      },
    },
    {
      id: 'actionKey',
      header: translate(i18nPath + 'list.actionName'),
      selectCellData: (r: ApprovalCase) => r.actionName,
      cellDataWrapperComponent: CustomCell,
      style: { flexBasis: 200 },
    },
    {
      id: 'object',
      header: translate(i18nPath + 'list.object'),
      selectCellData: (r: ApprovalCase) => ({ id: r.objectId, type: r.objectType }),
      cellDataWrapperComponent: CustomCell,
      style: { flexBasis: 200 },
    },
    {
      id: 'originator',
      header: translate(i18nPath + 'list.originator'),
      selectCellData: (r: ApprovalCase) => ({
        id: r.originatorIdentityId,
        // type: r.originatorIdentityType,
        type: getTypeOfIdentity(r.originatorIdentityType ?? 'SYSTEM'),
      }),
      cellDataWrapperComponent: CustomCell,
      style: { flexBasis: 200 },
    },
    {
      id: 'claimedBy',
      header: translate(i18nPath + 'list.claimedBy'),
      selectCellData: (r: ApprovalCase) =>
        r.claimedByIdentityId
          ? {
              id: r.claimedByIdentityId,
              type: getTypeOfIdentity(r.claimedByIdentityType ?? 'SYSTEM'),
            }
          : null,
      cellDataWrapperComponent: CustomCell,
      style: { flexBasis: 200 },
    },
    {
      id: 'steps',
      header: translate(i18nPath + 'list.step'),
      selectCellData: (r: ApprovalCase) => r,
      cellDataWrapperComponent: CustomCell,
      style: { flexBasis: 80 },
    },
  ];

  const getRowLink = (record: ApprovalCase) => `/approvals/queue/${record.id}/show`;

  const { hasNextPage, isNextPageLoading, data } = list;
  if (!permissions) return null;
  if (
    !permissions?.includes('approvalCase.list.pending.eligibleForDecision') &&
    !permissions?.includes('approvalCase.list.pending.all') &&
    !permissions.includes('approvalCase.admin')
  )
    return <NoPermissions />;
  if (isLoading) return <Loader />;
  if (!isFeatureEnabled) return <TenantLicensePaywall />;

  return (
    <VirtualizedListWrapper
      title={
        <Title
          resource={resource}
          headline={translate(i18nPath + 'list.pendingApprovalsTitle')}
          subtitle={translate(i18nPath + 'list.pendingApprovalsTitleSubtitle')}
        />
      }
      filters={<ListFilters cleanFilters={history.action !== 'POP'} />}
    >
      <Paper style={{ flex: '1 1 auto', overflow: 'hidden', ...fixBottomShadow }}>
        <VirtualizedList<ApprovalCase>
          resource={resource}
          columns={columns}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          filters={filters}
          data={data}
          loadNextPage={loadNextPage}
          outterTable
          rowHeight={48}
          getRowLink={getRowLink}
          minHeight={0}
        />
      </Paper>
    </VirtualizedListWrapper>
  );
};

const fixBottomShadow = { paddingBottom: 2 };

export default QueueApprovalsList;
